import { useState } from "react";
import { Loader2, Wand2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { sitePlacement, fmtFt } from "@/lib/walls";

export const DEFAULT_SITE = { lot_width: 60, lot_depth: 120, setback_front: 25, setback_rear: 20, setback_left: 5, setback_right: 5 };
const FIELDS = [["lot_width", "Lot width"], ["lot_depth", "Lot depth"], ["setback_front", "Front setback"],
  ["setback_rear", "Rear setback"], ["setback_left", "Left setback"], ["setback_right", "Right setback"]];

export const SiteFields = ({ value, onChange }) => (
  <div className="grid grid-cols-2 gap-2">
    {FIELDS.map(([k, label]) => (
      <label key={k} className="block">
        <span className="font-mono text-[9px] uppercase tracking-widest text-[var(--muted)]">{label} (ft)</span>
        <input type="number" min="0" step="0.5" value={value[k]} data-testid={`site-${k.replace("_", "-")}-input`}
          onChange={(e) => onChange({ ...value, [k]: e.target.value === "" ? "" : Number(e.target.value) })}
          className="mt-1 w-full bg-[var(--card)] border border-[var(--line)] focus:border-[var(--cyan)] outline-none px-2.5 py-2 text-sm font-mono transition-colors" />
      </label>
    ))}
  </div>
);

export const SitePanel = ({ project, onSaved, onFit, streaming }) => {
  const [site, setSite] = useState(project.site || DEFAULT_SITE);
  const [busy, setBusy] = useState(false);
  const plan = project.plan;
  const valid = FIELDS.every(([k]) => site[k] !== "" && site[k] >= 0);
  const sp = valid && plan ? sitePlacement(plan, site) : null;

  const save = async (next) => {
    setBusy(true);
    try {
      const { data } = await api.put(`/projects/${project.project_id}/site`, { site: next });
      onSaved(data.site);
      toast.success(next ? "Lot saved" : "Lot removed");
      return true;
    } catch (e) { toast.error(errMsg(e)); return false; } finally { setBusy(false); }
  };

  return (
    <div className="space-y-3" data-testid="site-panel">
      <SiteFields value={site} onChange={setSite} />
      {sp && (
        <div className={`text-xs font-mono px-3 py-2 border ${sp.fits ? "border-[var(--emerald)]/40 text-[var(--emerald)]" : "border-red-400/40 text-red-400"}`} data-testid="site-fit-status">
          Buildable {fmtFt(sp.bw)} × {fmtFt(sp.bd)} · {sp.fits ? "house fits" : "house exceeds setbacks"}
        </div>
      )}
      <div className="flex gap-2">
        <button data-testid="site-save-btn" disabled={busy || !valid} onClick={() => save(site)}
          className="flex-1 py-2 text-sm border border-[var(--line)] hover:border-[var(--cyan)] transition-colors disabled:opacity-40">
          {busy ? <Loader2 size={14} className="animate-spin mx-auto" /> : "Save lot"}
        </button>
        {project.site && (
          <button data-testid="site-remove-btn" disabled={busy} onClick={() => save(null)}
            className="px-3 py-2 text-sm border border-[var(--line)] text-[var(--muted)] hover:text-red-400 transition-colors">Remove</button>
        )}
      </div>
      {plan && (
        <button data-testid="site-fit-btn" disabled={busy || streaming || !valid}
          onClick={async () => { if (await save(site)) onFit("Refit this design so the footprint fits inside the buildable area of my lot. Keep the room program; add a storey if needed."); }}
          className="w-full flex items-center justify-center gap-2 py-2.5 text-sm font-semibold bg-[var(--amber)] text-[var(--bg)] hover:brightness-110 transition-[filter] disabled:opacity-40">
          <Wand2 size={15} /> Fit plan to lot with AI
        </button>
      )}
    </div>
  );
};
